/**
 * Custom hook for SSE-based iteration chat with thinking step updates.
 */

import { useCallback, useRef, useState } from 'react';
import { authenticatedFetch } from '../../../api/apiClient';
import {
  ThinkingStartedEventData,
  ThinkingStep,
  ToolCallCompletedEventData,
  ToolCallStartedEventData,
} from '../types/thinkingTypes';

interface UseIterationSSEOptions {
  onError?: (message: string) => void;
}

export interface IterationSSEResponse {
  iteration: any;
  thinkingSteps: ThinkingStep[];
}

interface UseIterationSSEResult {
  /** Send a message to the iteration chat via SSE */
  sendMessage: (
    iterationFqn: string,
    userInput: string
  ) => Promise<IterationSSEResponse | null>;
  /** Cancel the current SSE request */
  cancelRequest: () => void;
  /** Whether a request is currently in progress */
  isLoading: boolean;
  /** Current thinking steps for display */
  thinkingSteps: ThinkingStep[];
  /** Clear thinking steps */
  clearThinkingSteps: () => void;
}

/**
 * Hook for managing SSE-based iteration chat with real-time thinking updates.
 */
export function useIterationSSE({
  onError,
}: UseIterationSSEOptions): UseIterationSSEResult {
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const stepIdCounterRef = useRef(0);

  const generateStepId = useCallback(() => {
    stepIdCounterRef.current += 1;
    return `step-${stepIdCounterRef.current}`;
  }, []);

  const clearThinkingSteps = useCallback(() => {
    setThinkingSteps([]);
    stepIdCounterRef.current = 0;
  }, []);

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (
      iterationFqn: string,
      userInput: string
    ): Promise<IterationSSEResponse | null> => {
      // Cancel any existing request
      cancelRequest();

      // Reset state
      clearThinkingSteps();
      setIsLoading(true);

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Track steps locally to return with result (avoids stale closure issues)
      let localSteps: ThinkingStep[] = [];

      try {
        const response = await authenticatedFetch('/api/spark/iterations/chat/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify({
            iteration_fqn: iterationFqn,
            user_input: userInput,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed with status ${response.status}`);
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let result: IterationSSEResponse | null = null;

        // Helper to update both local tracking and state
        const updateSteps = (updater: (prev: ThinkingStep[]) => ThinkingStep[]) => {
          localSteps = updater(localSteps);
          setThinkingSteps(localSteps);
        };

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete events from buffer
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // Keep incomplete event in buffer

          for (const eventBlock of events) {
            if (!eventBlock.trim() || eventBlock.startsWith(':')) {
              continue; // Skip empty lines and comments (keepalive)
            }

            // Parse SSE event
            const lines = eventBlock.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventType || !eventData) {
              continue;
            }

            try {
              const data = JSON.parse(eventData);

              switch (eventType) {
                case 'thinking_started': {
                  const thinkingData = data as ThinkingStartedEventData;
                  updateSteps((prev) => [
                    ...prev,
                    {
                      id: generateStepId(),
                      type: 'thinking',
                      status: 'pending',
                      thinkingContent: thinkingData.thinking_content,
                      timestamp: Date.now(),
                    },
                  ]);
                  break;
                }

                case 'tool_call_started': {
                  const toolData = data as ToolCallStartedEventData;
                  updateSteps((prev) => {
                    // Mark previous thinking step as completed if exists
                    const updated = prev.map((step) =>
                      step.type === 'thinking' && step.status === 'pending'
                        ? { ...step, status: 'completed' as const }
                        : step
                    );
                    // Add new tool call step
                    return [
                      ...updated,
                      {
                        id: generateStepId(),
                        type: 'tool_call' as const,
                        toolName: toolData.tool_name,
                        toolArgs: toolData.tool_args,
                        status: 'pending' as const,
                        timestamp: Date.now(),
                      },
                    ];
                  });
                  break;
                }

                case 'tool_call_completed': {
                  const toolData = data as ToolCallCompletedEventData;
                  // Match by tool_name AND tool_args for parallel call support
                  updateSteps((prev) => {
                    let matched = false;
                    return prev.map((step) => {
                      if (
                        !matched &&
                        step.type === 'tool_call' &&
                        step.toolName === toolData.tool_name &&
                        step.status === 'pending' &&
                        JSON.stringify(step.toolArgs) === JSON.stringify(toolData.tool_args)
                      ) {
                        matched = true;
                        return { ...step, status: 'completed' as const, result: toolData.tool_result };
                      }
                      return step;
                    });
                  });
                  break;
                }

                case 'response_complete': {
                  // Mark all remaining steps as completed
                  updateSteps((prev) =>
                    prev.map((step) =>
                      step.status === 'pending' ? { ...step, status: 'completed' as const } : step
                    )
                  );
                  result = {
                    iteration: data.iteration,
                    thinkingSteps: localSteps,
                  };
                  break;
                }

                case 'error':
                  onError?.(data.message);
                  break;

                default:
                  console.warn('Unknown SSE event type:', eventType);
              }
            } catch (parseError) {
              console.error('Failed to parse SSE event data:', parseError, eventData);
            }
          }
        }

        return result;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Request was cancelled
          return null;
        }

        const message = error instanceof Error ? error.message : 'Unknown error occurred';
        onError?.(message);
        return null;
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [cancelRequest, clearThinkingSteps, generateStepId, onError]
  );

  return {
    sendMessage,
    cancelRequest,
    isLoading,
    thinkingSteps,
    clearThinkingSteps,
  };
}
